import type { Userbot } from "../../interfaces"

/** UI state may contain stale values; only the selected branch is serialized. */
export interface LaunchTargetState {
  executor: "client" | "role"
  clientId?: string
  role?: string
  roleMode?: Userbot.RoleMode
  targetUnit?: Userbot.TargetUnit
  targetFilter?: Userbot.TargetFilter | ""
  maxAgeDays?: string
}

export const ageFilter = (filter: LaunchTargetState["targetFilter"]) =>
  !!filter && filter !== "knownMemberships"

const age = (state: LaunchTargetState) =>
  state.maxAgeDays?.trim() ? Number(state.maxAgeDays) : 14

export const validateLaunchTarget = (state: LaunchTargetState): string[] => {
  const errors: string[] = []
  if (!(state.executor === "client" ? state.clientId : state.role)?.trim()) {
    errors.push("Выберите исполнителя: клиента или роль")
  }
  if (ageFilter(state.targetFilter)) {
    const maxAgeDays = age(state)
    if (!Number.isFinite(maxAgeDays) || maxAgeDays <= 0) {
      errors.push("maxAgeDays должен быть конечным числом > 0")
    }
  }
  return errors
}

export const serializeLaunchTarget = (
  state: LaunchTargetState,
): Userbot.LaunchTarget => {
  const errors = validateLaunchTarget(state)
  if (errors.length) throw new Error(errors.join("\n"))
  const target: Userbot.LaunchTarget =
    state.executor === "client"
      ? { clientId: state.clientId!.trim() }
      : {
          role: state.role!.trim(),
          ...(state.roleMode ? { roleMode: state.roleMode } : {}),
          ...(state.targetUnit ? { targetUnit: state.targetUnit } : {}),
        }
  if (state.targetFilter) target.targetFilter = state.targetFilter
  if (ageFilter(state.targetFilter))
    target.targetFilterParams = { maxAgeDays: age(state) }
  return target
}

export const launchTargetFromDto = (dto: {
  clientId?: string | null
  role?: string | null
  roleMode?: Userbot.RoleMode
  targetUnit?: Userbot.TargetUnit
  targetFilter?: Userbot.TargetFilter | null
  targetFilterParams?: { maxAgeDays?: number } | null
}): LaunchTargetState => ({
  ...(dto.role
    ? {
        executor: "role" as const,
        role: dto.role,
        ...(dto.roleMode ? { roleMode: dto.roleMode } : {}),
        ...(dto.targetUnit ? { targetUnit: dto.targetUnit } : {}),
      }
    : { executor: "client" as const, clientId: dto.clientId ?? "" }),
  ...(dto.targetFilter ? { targetFilter: dto.targetFilter } : {}),
  ...(ageFilter(dto.targetFilter ?? undefined) &&
  dto.targetFilterParams?.maxAgeDays !== undefined
    ? { maxAgeDays: String(dto.targetFilterParams.maxAgeDays) }
    : {}),
})

export const switchLaunchExecutor = (
  state: LaunchTargetState,
  executor: LaunchTargetState["executor"],
): LaunchTargetState => ({
  executor,
  ...(executor === "client" ? { clientId: "" } : { role: "" }),
  ...(state.targetFilter ? { targetFilter: state.targetFilter } : {}),
  ...(ageFilter(state.targetFilter) && state.maxAgeDays !== undefined
    ? { maxAgeDays: state.maxAgeDays }
    : {}),
})
